import { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { CatchResponse } from '../types/api';
import { catchMarkerIcon } from '../lib/leafletIcon';

interface CatchesMapProps {
  catches: CatchResponse[];
  height?: string;
  className?: string;
  onCatchClick?: (c: CatchResponse) => void;
  clubName?: string | null;
}

function BoundsHandler({ catches }: { catches: CatchResponse[] }) {
  const map = useMap();

  useEffect(() => {
    const points = catches.map((c) => L.latLng(c.latitude, c.longitude));
    map.fitBounds(L.latLngBounds(points), { padding: [30, 30] });
  }, [catches, map]);

  return null;
}

export function CatchesMap({ catches, height = '400px', className, onCatchClick, clubName }: CatchesMapProps) {
  const wrapperClass = ['rounded-xl', 'overflow-hidden', className].filter(Boolean).join(' ');

  if (catches.length === 0) {
    return (
      <div className={wrapperClass} style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        No catches with locations
      </div>
    );
  }

  const first = catches[0];

  return (
    <div className={wrapperClass} style={{ height }}>
      <MapContainer center={[first.latitude, first.longitude]} zoom={catches.length === 1 ? 13 : 10} style={{ height: '100%' }}>
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        {catches.length >= 2 && <BoundsHandler catches={catches} />}
        {catches.map((c) => (
          <Marker key={c.id} position={[c.latitude, c.longitude]} icon={catchMarkerIcon}>
            <Popup>
              <div style={{ minWidth: '140px' }}>
                <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '2px' }}>{c.fishTypeName}</div>
                {clubName && (
                  <div style={{ fontSize: '11px', color: '#0d9488', fontWeight: 500, marginBottom: '2px' }}>
                    [{clubName}] {c.userDisplayName}
                  </div>
                )}
                {c.spotName && <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '2px' }}>{c.spotName}</div>}
                {c.lengthInches != null && c.lengthInches > 0 && (
                  <div style={{ fontSize: '12px', color: '#0d9488', marginBottom: '4px' }}>{c.lengthInches}&Prime;</div>
                )}
                <div style={{ fontSize: '11px', color: '#94a3b8' }}>{new Date(c.caughtAt).toLocaleDateString()}</div>
                {onCatchClick && (
                  <button
                    onClick={() => onCatchClick(c)}
                    style={{ marginTop: '8px', fontSize: '12px', color: '#0d9488', fontWeight: 500, cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
                  >
                    View catch →
                  </button>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
